import { useEffect, useState } from 'react'
import { PageHeader } from '../../components/PageHeader'
import { supabase } from '../../lib/supabase'
import { useAuth } from '../../lib/auth'
import { useT } from '../../i18n'
import { readError } from '../../lib/errors'
import type { NotificationPreferencesRow } from '../../lib/database.types'

interface Props {
  onBack: () => void
}

type PrefKey = Exclude<keyof NotificationPreferencesRow, 'profile_id' | 'updated_at'>

interface PrefRow {
  key: PrefKey
  label: string
  sub: string
  /** Rendered indented under `parent`, and inert while the parent is off. */
  parent?: PrefKey
  /** Game names stay English in every locale, so a row can opt out of t(). */
  literalLabel?: boolean
}

const SECTIONS: Array<{ title: string; rows: PrefRow[] }> = [
  {
    title: 'Weekly event',
    rows: [
      { key: 'weekly_event_open',  label: 'New week opens',
        sub: 'Email me Monday morning when the new weekly games open.' },
      { key: 'weekly_event_close', label: 'Voting opens',
        sub: 'Email me when last week’s entries are ready to view and vote on.' },
    ],
  },
  {
    title: 'My submissions',
    rows: [
      { key: 'my_story_voted', label: 'Someone voted for my submission',
        sub: 'Bundled to once per day so it’s never spammy.' },
      { key: 'my_story_won',   label: 'I won a trophy',
        sub: 'A note from us when you take a trophy.' },
    ],
  },
  {
    title: 'Online play',
    rows: [
      { key: 'online_turn', label: 'Your turn in online matches',
        sub: 'A push when a matchmade game is waiting on your move.' },
    ],
  },
  {
    title: 'Reminders',
    rows: [
      { key: 'weekly_reminders', label: 'Weekly reminders',
        sub: 'A nudge if you have not entered this week’s games, or played today’s dailies. Only for games you have played before.' },
      { key: 'remind_ideatthat',    parent: 'weekly_reminders', literalLabel: true, label: 'I’D EAT THAT',
        sub: 'Saturday, if this week’s story is missing.' },
      { key: 'remind_colorful',     parent: 'weekly_reminders', literalLabel: true, label: 'COLORFUL',
        sub: 'Saturday, if this week’s picks are missing.' },
      { key: 'remind_three_step',   parent: 'weekly_reminders', literalLabel: true, label: 'THREE STEP SOLUTION',
        sub: 'Saturday, if this week’s solution is missing.' },
      { key: 'remind_fake_out',     parent: 'weekly_reminders', literalLabel: true, label: 'FAKE OUT',
        sub: 'Evenings, if today’s puzzle is unplayed.' },
      { key: 'remind_close_enough', parent: 'weekly_reminders', literalLabel: true, label: 'CLOSE ENOUGH',
        sub: 'Evenings, if today’s puzzle is unplayed.' },
    ],
  },
]

export function NotificationsScreen({ onBack }: Props) {
  const { profile } = useAuth()
  const t = useT()
  const [prefs, setPrefs] = useState<NotificationPreferencesRow | null>(null)
  const [savingKey, setSavingKey] = useState<PrefKey | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!profile?.id) return
    let cancelled = false
    ;(async () => {
      const { data, error: e } = await supabase
        .from('notification_preferences')
        .select('*')
        .eq('profile_id', profile.id)
        .maybeSingle()
      if (cancelled) return
      if (e) { setError(readError(e)); return }
      setPrefs(data ?? null)
    })()
    return () => { cancelled = true }
  }, [profile?.id])

  async function toggle(key: PrefKey) {
    if (!profile?.id || !prefs || savingKey) return
    const next = !prefs[key]
    // Optimistic.
    setPrefs({ ...prefs, [key]: next })
    setSavingKey(key); setError(null)
    try {
      const patch: Partial<NotificationPreferencesRow> = {
        [key]: next,
        updated_at: new Date().toISOString(),
      }
      const { error: e } = await supabase
        .from('notification_preferences')
        .update(patch)
        .eq('profile_id', profile.id)
      if (e) throw e
    } catch (e) {
      // Roll back.
      setPrefs(p => p ? { ...p, [key]: !next } : p)
      setError(readError(e))
    } finally {
      setSavingKey(null)
    }
  }

  return (
    <div className="screen pb-12">
      <PageHeader title={t('Notifications')} onBack={onBack} />

      <div className="px-5 py-5 space-y-6 overflow-y-auto">
        <p className="text-sm text-brand-muted">
          {t('Choose what you want to be notified about. You can change these any time.')}
        </p>

        {error && (
          <div className="card p-3 bg-brand-danger/10 border border-brand-danger/30">
            <p className="text-sm text-brand-danger">{error}</p>
          </div>
        )}

        {SECTIONS.map(section => (
          <div key={section.title}>
            <p className="text-xs font-medium text-brand-muted uppercase tracking-wide mb-2">
              {t(section.title)}
            </p>
            <div className="space-y-2">
              {section.rows.map(r => {
                // A child row is inert while its parent is off: it keeps
                // its own value (so turning the parent back on restores
                // the player's choices) but reads as unavailable.
                const parentOff = r.parent ? !prefs?.[r.parent] : false
                return (
                  <label key={r.key}
                         className={`card p-4 flex items-start gap-3 cursor-pointer
                                     active:bg-brand-elevated transition-colors
                                     ${r.parent ? 'ml-6' : ''} ${parentOff ? 'opacity-50' : ''}`}>
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-brand-text">
                        {r.literalLabel ? r.label : t(r.label)}
                      </p>
                      <p className="text-xs text-brand-muted mt-0.5">{t(r.sub)}</p>
                    </div>
                    <Toggle
                      checked={!!prefs?.[r.key]}
                      onChange={() => toggle(r.key)}
                      disabled={!prefs || savingKey === r.key || parentOff}
                    />
                  </label>
                )
              })}
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

function Toggle({
  checked, onChange, disabled,
}: { checked: boolean; onChange: () => void; disabled?: boolean }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      onClick={onChange}
      disabled={disabled}
      className={`relative w-12 h-7 rounded-full transition-colors shrink-0
                  disabled:opacity-50
                  ${checked ? 'bg-brand-lime-dark' : 'bg-brand-border'}`}
    >
      <span className={`absolute top-0.5 left-0.5 w-6 h-6 bg-white rounded-full shadow-xs
                        transition-transform ${checked ? 'translate-x-5' : ''}`} />
    </button>
  )
}
